import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  deleteRecord,
  fetchContent,
  fetchDiff,
  proposePr,
  putRecord,
  putSite,
  runValidate,
} from './api';

interface Content {
  site: Record<string, unknown>;
  strategies: Array<Record<string, unknown>>;
  articles: Array<Record<string, unknown>>;
  tiers: Array<Record<string, unknown>>;
  team: Array<Record<string, unknown>>;
  pages: Record<string, Record<string, unknown>>;
}

const Layout = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  min-height: 100vh;
  background: ${(p) => p.theme.color.bg[0]};
  color: ${(p) => p.theme.color.text[0]};
  font-family: ${(p) => p.theme.font.sans};
`;

const Side = styled.aside`
  border-right: 1px solid ${(p) => p.theme.color.border.faint};
  padding: ${(p) => p.theme.step[5]}px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Brand = styled(Link)`
  font-weight: 700;
  margin-bottom: ${(p) => p.theme.step[5]}px;
  color: ${(p) => p.theme.color.accent.amber};
`;

const SideLink = styled(NavLink)`
  display: block;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  color: ${(p) => p.theme.color.text[1]};

  &.active { background: ${(p) => p.theme.color.bg[2]}; color: ${(p) => p.theme.color.text[0]}; }
  &:hover { color: ${(p) => p.theme.color.text[0]}; }
`;

const Content = styled.section`
  padding: ${(p) => p.theme.step[6]}px;
`;

const TopBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: ${(p) => p.theme.step[5]}px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  background: ${(p) => (p.$primary ? p.theme.color.accent.amber : p.theme.color.bg[2])};
  color: ${(p) => (p.$primary ? p.theme.color.bg[0] : p.theme.color.text[0])};
  border: 1px solid
    ${(p) => (p.$primary ? p.theme.color.accent.amber : p.theme.color.border.med)};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td { padding: 8px 10px; border-bottom: 1px solid ${(p) => p.theme.color.border.faint}; text-align: left; }
  th { color: ${(p) => p.theme.color.text[3]}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 480px;
  background: ${(p) => p.theme.color.bg[1]};
  color: ${(p) => p.theme.color.text[0]};
  border: 1px solid ${(p) => p.theme.color.border.med};
  border-radius: 8px;
  padding: 12px;
  font-family: ${(p) => p.theme.font.mono};
  font-size: 13px;
`;

const Status = styled.div<{ $ok?: boolean }>`
  font-family: ${(p) => p.theme.font.mono};
  font-size: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${(p) => (p.$ok ? p.theme.color.semantic.upBg : p.theme.color.semantic.downBg)};
  color: ${(p) => (p.$ok ? p.theme.color.semantic.up : p.theme.color.semantic.down)};
  white-space: pre-wrap;
`;

function useFetchedContent() {
  const [content, setContent] = useState<Content | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReload] = useState(0);

  useEffect(() => {
    fetchContent()
      .then((c) => setContent(c as Content))
      .catch((e: Error) => setError(e.message));
  }, [reloadKey]);

  return { content, error, reload: () => setReload((k) => k + 1) };
}

function CollectionView({
  collectionKey,
}: {
  collectionKey: 'strategies' | 'articles' | 'tiers' | 'team';
}) {
  const { content, error, reload } = useFetchedContent();
  if (error) return <p>Failed to load: {error}</p>;
  if (!content) return <p>Loading…</p>;
  const items = content[collectionKey];
  return (
    <Content>
      <h2 style={{ marginTop: 0, textTransform: 'capitalize' }}>{collectionKey}</h2>
      <p style={{ color: 'var(--text-2)' }}>{items.length} record(s)</p>
      <Table>
        <thead>
          <tr>
            <th>id</th>
            <th>name / title</th>
            <th>actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const id = String(r.id ?? '');
            const display = String(r.name ?? r.title ?? id);
            return (
              <tr key={id}>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{id}</td>
                <td>{display}</td>
                <td>
                  <Btn as={Link} to={`/admin/${collectionKey}/${id}`}>
                    Edit
                  </Btn>
                  <Btn
                    style={{ marginLeft: 8 }}
                    onClick={async () => {
                      if (!confirm(`Delete ${id}?`)) return;
                      await deleteRecord(collectionKey, id);
                      reload();
                    }}
                  >
                    Delete
                  </Btn>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Content>
  );
}

function RecordEditor({
  collectionKey,
}: {
  collectionKey: 'strategies' | 'articles' | 'tiers' | 'team' | 'pages' | 'site';
}) {
  const { id = '' } = useParams<{ id: string }>();
  const { content, error } = useFetchedContent();
  const [text, setText] = useState<string>('');
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!content) return;
    let record: unknown;
    if (collectionKey === 'site') record = content.site;
    else if (collectionKey === 'pages') record = content.pages[id];
    else record = (content[collectionKey] as Array<Record<string, unknown>>).find((r) => r.id === id);
    setText(JSON.stringify(record, null, 2));
  }, [content, collectionKey, id]);

  if (error) return <p>Failed to load: {error}</p>;
  if (!content) return <p>Loading…</p>;

  const save = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setStatus({ ok: false, msg: `Invalid JSON: ${(e as Error).message}` });
      return;
    }
    const result =
      collectionKey === 'site'
        ? await putSite(parsed)
        : await putRecord(collectionKey, id, parsed);
    if ((result as { ok?: boolean }).ok) {
      setStatus({ ok: true, msg: 'Saved.' });
    } else {
      const errs = (result as { errors?: Array<{ path: (string | number)[]; message: string }>; error?: string });
      const msg =
        errs.errors?.map((e) => `${e.path.join('.')}: ${e.message}`).join('\n') ??
        errs.error ??
        'Unknown error';
      setStatus({ ok: false, msg });
    }
  };

  return (
    <Content>
      <TopBar>
        <Btn onClick={() => nav(-1)}>Back</Btn>
        <h2 style={{ margin: 0 }}>
          {collectionKey} / {id}
        </h2>
        <Btn $primary onClick={save}>
          Save
        </Btn>
      </TopBar>
      <TextArea value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
      {status && (
        <Status $ok={status.ok} style={{ marginTop: 12 }}>
          {status.msg}
        </Status>
      )}
    </Content>
  );
}

function ProposeView() {
  const [diff, setDiff] = useState('');
  const [validateStatus, setValidateStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [title, setTitle] = useState('Content updates from admin');
  const [body, setBody] = useState('');
  const [proposeStatus, setProposeStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    fetchDiff().then(setDiff);
  }, []);

  const validate = async () => {
    const r = await runValidate();
    setValidateStatus({
      ok: r.ok,
      msg: r.ok ? r.output ?? '✓ Valid' : (r.stdout ?? '') + '\n' + (r.stderr ?? ''),
    });
  };

  const propose = async () => {
    const r = await proposePr({ title, body });
    if (r.ok) {
      setProposeStatus({
        ok: true,
        msg: r.prUrl
          ? `PR opened: ${r.prUrl}`
          : `Branch pushed: ${r.branch ?? '(unknown)'}\n(no GH_TOKEN — open the PR manually)`,
      });
    } else {
      setProposeStatus({ ok: false, msg: r.error ?? 'Unknown error' });
    }
  };

  return (
    <Content>
      <h2 style={{ marginTop: 0 }}>Propose changes</h2>
      <p style={{ color: 'var(--text-2)' }}>
        Validate the full content tree, review the diff, and open a PR with the staged JSON
        changes.
      </p>
      <TopBar>
        <Btn onClick={validate}>Validate</Btn>
      </TopBar>
      {validateStatus && <Status $ok={validateStatus.ok}>{validateStatus.msg}</Status>}

      <h3 style={{ marginTop: 24 }}>Diff</h3>
      <pre
        style={{
          background: 'var(--bg-1)',
          padding: 12,
          borderRadius: 8,
          maxHeight: 320,
          overflow: 'auto',
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
        }}
      >
        {diff || '(no changes in content/)'}
      </pre>

      <h3 style={{ marginTop: 24 }}>Open PR</h3>
      <label style={{ display: 'block', marginBottom: 8 }}>
        Title
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ width: '100%', padding: 8, marginTop: 4 }}
        />
      </label>
      <label style={{ display: 'block', marginBottom: 8 }}>
        Body
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          style={{ width: '100%', padding: 8, marginTop: 4, fontFamily: 'var(--font-mono)' }}
        />
      </label>
      <Btn $primary onClick={propose}>
        Propose PR
      </Btn>
      {proposeStatus && (
        <Status $ok={proposeStatus.ok} style={{ marginTop: 12 }}>
          {proposeStatus.msg}
        </Status>
      )}
    </Content>
  );
}

function PagesIndex() {
  const { content, error } = useFetchedContent();
  if (error) return <p>Failed to load: {error}</p>;
  if (!content) return <p>Loading…</p>;
  const slugs = Object.keys(content.pages);
  return (
    <Content>
      <h2 style={{ marginTop: 0 }}>Pages</h2>
      <Table>
        <thead>
          <tr>
            <th>slug</th>
            <th>title</th>
            <th>actions</th>
          </tr>
        </thead>
        <tbody>
          {slugs.map((slug) => {
            const p = content.pages[slug];
            return (
              <tr key={slug}>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{slug}</td>
                <td>{String((p?.seo as Record<string, unknown>)?.title ?? '')}</td>
                <td>
                  <Btn as={Link} to={`/admin/pages/${slug}`}>
                    Edit
                  </Btn>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Content>
  );
}

function Sidebar() {
  return (
    <Side>
      <Brand to="/admin">PowerQuant Admin</Brand>
      <SideLink to="/admin/strategies">Strategies</SideLink>
      <SideLink to="/admin/articles">Articles</SideLink>
      <SideLink to="/admin/tiers">Tiers</SideLink>
      <SideLink to="/admin/team">Team</SideLink>
      <SideLink to="/admin/pages">Pages</SideLink>
      <SideLink to="/admin/site">Site</SideLink>
      <SideLink to="/admin/propose">Propose PR</SideLink>
      <Link
        to="/"
        style={{ marginTop: 'auto', fontSize: 12, opacity: 0.6 }}
      >
        ← Back to site
      </Link>
    </Side>
  );
}

function HomeView() {
  return (
    <Content>
      <h1 style={{ marginTop: 0 }}>Admin</h1>
      <p style={{ color: 'var(--text-2)' }}>
        Local-only editor for the JSON content tree under <code>content/</code>.
      </p>
      <ul>
        <li>Pick a collection in the sidebar to list and edit records.</li>
        <li>Save is gated by the same Zod schemas as the build.</li>
        <li>
          When ready, hit <strong>Propose PR</strong> to push your changes on a new branch and
          open a draft pull request.
        </li>
      </ul>
    </Content>
  );
}

export default function AdminApp() {
  const titleSetOnce = useMemo(() => {
    if (typeof document !== 'undefined') document.title = 'PowerQuant · Admin';
    return true;
  }, []);
  void titleSetOnce;

  return (
    <Layout>
      <Sidebar />
      <Routes>
        <Route index element={<HomeView />} />
        <Route path="strategies" element={<CollectionView collectionKey="strategies" />} />
        <Route path="strategies/:id" element={<RecordEditor collectionKey="strategies" />} />
        <Route path="articles" element={<CollectionView collectionKey="articles" />} />
        <Route path="articles/:id" element={<RecordEditor collectionKey="articles" />} />
        <Route path="tiers" element={<CollectionView collectionKey="tiers" />} />
        <Route path="tiers/:id" element={<RecordEditor collectionKey="tiers" />} />
        <Route path="team" element={<CollectionView collectionKey="team" />} />
        <Route path="team/:id" element={<RecordEditor collectionKey="team" />} />
        <Route path="pages" element={<PagesIndex />} />
        <Route path="pages/:id" element={<RecordEditor collectionKey="pages" />} />
        <Route path="site" element={<RecordEditor collectionKey="site" />} />
        <Route path="propose" element={<ProposeView />} />
      </Routes>
    </Layout>
  );
}
